import { ApolloError } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import { LogoutError } from '../components/error-boundry/LogoutError'

/**
 * The environment name to use when sending messages/events to Sentry.
 * Should be passed as the `environment` to the Sentry config.
 *
 * @see https://docs.sentry.io/platforms/javascript/configuration/options/#environment
 */
type SentryEnv = 'development' | 'test' | 'production'

/**
 * @returns The {@link SentryEnv | Sentry environment name }
 * used to tag events sent to Sentry.
 */
function getSentryEnv(): SentryEnv {
  // Default to production;
  // We prefer getting messages we don't need in dev,
  // as opposed to missing messages we want in prod.
  // NODE_ENV is set by React.
  let sentryEnv: SentryEnv = 'production'
  if (process.env.NODE_ENV === 'development') {
    sentryEnv = 'development'
  } else if (process.env.NODE_ENV === 'test') {
    sentryEnv = 'test'
  }
  return sentryEnv
}

export function sentryInit(): void {
  Sentry.init({
    dsn: 'https://f742ca507224d02f4cfc001031bad260@o4508642798534656.ingest.de.sentry.io/4508646645432400',
    integrations: [Sentry.browserTracingIntegration()],
    environment: getSentryEnv(),
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.0,
    attachStacktrace: true,
    beforeSend: (event) => {
      // We throw the logout error in the error link at the same time as we do a redirect to the logout page (see explanation there)
      // As we do the redirect to logout we will in most case leave the page before the error get handled.
      // If this happen Sentry logs the exception has an unhalded error. But for this particular case this is the expected behavior.
      // So we want to change the level to warning if the error is a logout error
      if (event.exception?.values?.length === 1) {
        const exception = event.exception?.values[0]
        if (exception.value === new LogoutError().message) {
          return {
            ...event,
            level: 'warning',
          }
        }
      }
      return event
    },
  })
}

export function sentryClose(): void {
  Sentry.close()
}

// graphQLErrors errors have a code
interface ErrorWithCode extends Error {
  code: number
}

function isErrorWithCode(err: unknown): err is ErrorWithCode {
  return (err as ErrorWithCode).code !== undefined
}

// network errors have a statusCode
interface ErrorWithStatusCode extends Error {
  statusCode: number
}

function isErrorWithStatusCode(err: unknown): err is ErrorWithStatusCode {
  return (err as ErrorWithStatusCode).statusCode !== undefined
}

function isUnauthorizedError(error: unknown): boolean {
  // unauthorized error are thrown by the apollo client in an apollo error
  if (!(error instanceof ApolloError)) {
    return false
  }
  // They can either be in a graphql error or a network error
  // If there are multiple graphql error there is probably one that is not a unauthorized error
  if (error.graphQLErrors.length === 1) {
    const err = error.graphQLErrors[0]
    if (isErrorWithCode(err)) {
      return err.code === 401 || err.code === 403
    }
  }
  if (error.networkError) {
    const err = error.networkError
    if (isErrorWithStatusCode(err)) {
      return err.statusCode === 401 || err.statusCode === 403
    }
  }

  return false
}

export function isLogoutError(error: unknown): boolean {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.length === 1 &&
    error.graphQLErrors[0].extensions instanceof LogoutError
  )
}

export function captureException(
  err: unknown,
  level?: Sentry.SeverityLevel,
): void {
  if (level) {
    Sentry.captureException(err, { level })
  } else if (isUnauthorizedError(err) || isLogoutError(err)) {
    Sentry.captureException(err, { level: 'warning' })
  } else {
    Sentry.captureException(err)
  }
}
